/**
 * ROOF ROUND E5 r2 — ROOF-TO-ROOF JOIN (docs/MODELER.md "ROOF ROUND E5").
 * Supersedes r1's footprint member-trimming, which Matt rejected on sight: point
 * suppression broke the tails' even end-to-end rhythm and hit both sides of the
 * roof, and the BODIES still overlapped. The join model does what he described:
 * "one roof structure joins the other and adopts the eave settings, running all
 * the way out to the end of the joined eaves, when they are in alignment" — and
 * the joiner's BODY dies into the target at true valleys.
 *
 * ONE stored pick (`joinRoofId`, the walls-attach picked-target doctrine — a
 * stated per-roof intent, never auto-detected) drives three derivations:
 *
 * 1. BODY CLIP (joiner side): each joiner face ring loses the region where the
 *    TARGET's top surface rides above its own — the boundary of that region IS
 *    the valley line (both surfaces are piecewise planes, so it's piecewise
 *    straight), and everything below the seam tucks inside the target's mass.
 *    Faces whose BEARING planes are coplanar with a target face are SKIPPED
 *    (aligned eaves must not eat each other); untouched faces keep their
 *    original ring objects so the shared-edge census stays exact.
 *
 * 2. ALIGNED EAVES (both sides — the target derives the join in REVERSE by
 *    scanning for roofs that name it): eave edges whose bearing lines are
 *    collinear with a partner's, at the same overhang/slope/plate and facing
 *    the same way, lay their tail stations over the MERGED span in one rhythm;
 *    each roof emits only its own stretch and the TARGET owns overlaps.
 *
 * 3. ADOPTION + RUN CLIPPING (joiner side): the joiner reads its eave-member
 *    SETTINGS (sizes/spacing/styles/materials) from the target — the style
 *    authority — while the on/off toggles stay per-roof (they also drive the
 *    mass's decking read, which byId-less consumers derive independently); its
 *    fascia/gutter runs and outlooker brackets clip against the target's
 *    footprint (the r1 machinery, kept where it was RIGHT — boards butt the
 *    target's with a square cut, buried rakes drop their barge).
 *
 * Everything here is exception-proof cross-element reading: a broken target
 * degrades to "no join", never a blank model.
 */
import type { Pt2, ResolvedElement } from '@aj/bim-kernel'
import { clipperReady, differenceClosed, inflateClosedRing, intersectClosed, signedArea } from '../../clipper'
import { solveRoofForElement, collinearOverlap, type ResolvedRoofSolve, type RoofFace } from './solve'
import { pointInRing, spanOnBearingLine, specLines, uncoveredIntervals, type SeamFace, type SpecLine } from './framing'

const AREA_EPS = 1e-6
const LINE_TOL = 1e-3

export interface AlignedEave {
  /** OWN bearing edge line (E5 r3 — matched GEOMETRICALLY in the tails loop.
   *  Historical reason: face.edgeIndex used to leak the solver's NORMALIZED
   *  spec order, which on CW-drawn boundaries was NOT `solved.specs` order, so
   *  the index-keyed lookup silently missed on Matt's real roofs. 2026-08-19:
   *  solveRoofFaces now remaps edgeIndex to input order, so the index would
   *  work — the geometric match stays as belt-and-suspenders.) */
  ownA: Pt2
  ownD: Pt2
  ownInward: Pt2
  /** the partner's BEARING edge extent on the shared line (the merged array's
   *  END points — members land at the run's true wall corners) */
  otherA: Pt2
  otherB: Pt2
  /** the partner's SHEET extent on the shared line — its matched face's ring
   *  crossings, which run PAST the wall corners over its rake bands (E5 r4:
   *  the merged run must cover that stretch too and the partner owns it, or a
   *  union station landing there is orphaned — Matt's two-bay junction gap) */
  otherSheetA: Pt2
  otherSheetB: Pt2
  /** the partner's matched edge resolves tails on (merge only joins live runs) */
  otherTails: boolean
  /** the partner owns overlapping stations (true on the joiner's side) */
  otherPrimary: boolean
}

// SeamFace + seamKeptIntervals live in framing.ts (the shared low-level home,
// like specLines — join.ts imports framing values, so the helper can't live
// here without a value cycle).

export interface RoofJoinCtx {
  /** the join target's element — the joiner's style authority (null on the target side) */
  authority: ResolvedElement | null
  /** joiner side: the target's solve (body clip input; its BEARING ring is the
   *  wall line trim runs die at — r8) */
  targetSolved: ResolvedRoofSolve | null
  /** the aligned-eave pairs — one entry per OWN bearing edge with a partner */
  aligned: AlignedEave[]
  /** the partner's top-surface faces (joiner side: the target's; target side:
   *  each reverse joiner's CLIPPED faces) — the seam-suppression probes */
  seamFaces: SeamFace[]
}

/** eave edges align when their bearing lines are collinear, facing the same way,
 *  at the same overhang, slope, and plate — i.e. the EAVE LINES coincide in 3D */
function matchAligned(
  own: SpecLine[], ownSolved: ResolvedRoofSolve,
  other: SpecLine[], otherSolved: ResolvedRoofSolve,
  otherPrimary: boolean,
  out: AlignedEave[],
): void {
  if (Math.abs(ownSolved.zPlateFt - otherSolved.zPlateFt) > 1e-6) return
  const used = new Set<number>()
  for (const en of out) {
    // an own edge already matched by an earlier partner keeps its first match
    const i = own.find((L) => L.spec.a[0] === en.ownA[0] && L.spec.a[1] === en.ownA[1])
    if (i) used.add(i.i)
  }
  for (const L of own) {
    if (L.spec.gable || used.has(L.i)) continue
    for (const M of other) {
      if (M.spec.gable) continue
      if (Math.abs(L.spec.slopeRise - M.spec.slopeRise) > 1e-9) continue
      if (Math.abs(L.spec.overhangFt - M.spec.overhangFt) > 1e-9) continue
      if (L.n[0] * M.n[0] + L.n[1] * M.n[1] < 0.999) continue
      const dOf = (p: Pt2) => Math.abs(L.d[0] * (p[1] - L.spec.a[1]) - L.d[1] * (p[0] - L.spec.a[0]))
      if (dOf(M.spec.a) > LINE_TOL || dOf(M.spec.b) > LINE_TOL) continue
      // the partner's SHEET extent on the shared line: its matched faces' ring
      // crossings of the bearing line (falls back to the bearing extent)
      const inward: Pt2 = [-M.n[0], -M.n[1]]
      const eLine: Pt2 = [M.d[0], M.d[1]]
      const lineC = M.spec.a[0] * inward[0] + M.spec.a[1] * inward[1]
      let s0 = Infinity, s1 = -Infinity
      for (const g of otherSolved.faces) {
        if (g.uphill[0] * inward[0] + g.uphill[1] * inward[1] < 0.999) continue
        if (dOf(g.origin) > LINE_TOL) continue
        const span = spanOnBearingLine(g.ring, g.origin, g.uphill, eLine)
        if (!span) continue
        s0 = Math.min(s0, span[0])
        s1 = Math.max(s1, span[1])
      }
      const onLine = (s: number): Pt2 => [eLine[0] * s + inward[0] * lineC, eLine[1] * s + inward[1] * lineC]
      const hasSheet = s0 <= s1
      out.push({
        ownA: [L.spec.a[0], L.spec.a[1]],
        ownD: [L.d[0], L.d[1]],
        ownInward: [-L.n[0], -L.n[1]],
        otherA: [M.spec.a[0], M.spec.a[1]],
        otherB: [M.spec.b[0], M.spec.b[1]],
        otherSheetA: hasSheet ? onLine(s0) : [M.spec.a[0], M.spec.a[1]],
        otherSheetB: hasSheet ? onLine(s1) : [M.spec.b[0], M.spec.b[1]],
        otherTails: M.spec.tails === true,
        otherPrimary,
      })
      used.add(L.i)
      break
    }
  }
}

/**
 * E5 r6 — MUTUAL-JOIN guard: a pair where each roof names the other would make
 * BOTH sides joiners (both adopting the other's settings, both clipping — the
 * live symptom: Matt's big roof silently derived its outlookers from the small
 * roof's spacing). The pick flow now refuses to create mutuals; for data that
 * already holds one, exactly ONE side acts as the joiner, chosen
 * deterministically (the lexically smaller id), and the other is a pure target.
 */
export function activeJoinTargetId(el: ResolvedElement, byId: Map<string, ResolvedElement>): string | null {
  const jid = el.params.joinRoofId
  if (typeof jid !== 'string' || !jid || jid === el.id) return null
  const target = byId.get(jid)
  if (!target || target.category !== 'roof') return null
  if (target.params.joinRoofId === el.id && !(el.id < target.id)) return null   // mutual: the other side wins
  return jid
}

/**
 * The join context for a roof — both directions from the one stored pick:
 * joiner side from its own `joinRoofId`, target side by reverse scan (the
 * partner rhythm must be shared or the merged runs drift apart).
 */
export function resolveRoofJoin(
  el: ResolvedElement,
  solved: ResolvedRoofSolve,
  byId?: Map<string, ResolvedElement>,
): RoofJoinCtx | null {
  if (!byId) return null
  const aligned: AlignedEave[] = []
  const seamFaces: SeamFace[] = []
  let authority: ResolvedElement | null = null
  let targetSolved: ResolvedRoofSolve | null = null
  const ownLines = specLines(solved)
  const seamFacesOf = (owner: ResolvedElement, faces: RoofFace[]) => {
    const t = owner.assemblyThicknessFt
    for (const g of faces) {
      const dv = t * Math.sqrt(1 + g.m * g.m)
      seamFaces.push({ ring: g.ring, zTopAt: (p: Pt2) => g.zAt(p) + dv })
    }
  }

  // joiner side (r6: mutual pairs resolve to ONE active direction)
  const jid = activeJoinTargetId(el, byId)
  if (jid) {
    const target = byId.get(jid)
    if (target && target.category === 'roof') {
      try {
        const tgtSolved = solveRoofForElement(target)
        if (tgtSolved && tgtSolved.faces.length) {
          authority = target
          targetSolved = tgtSolved
          matchAligned(ownLines, solved, specLines(tgtSolved), tgtSolved, true, aligned)
          seamFacesOf(target, tgtSolved.faces)
        }
      } catch { /* a broken target degrades to no join */ }
    }
  }

  // target side (reverse): roofs that name THIS roof share their rhythm with it —
  // and their CLIPPED sheets drive this roof's own seam suppression (its sheet
  // edge stops drawing where the joiner's coplanar sheet carries on)
  byId.forEach((j) => {
    if (j.category !== 'roof' || j.id === el.id) return
    if (activeJoinTargetId(j, byId) !== el.id) return
    try {
      const jSolved = solveRoofForElement(j)
      if (jSolved && jSolved.faces.length) {
        matchAligned(ownLines, solved, specLines(jSolved), jSolved, false, aligned)
        seamFacesOf(j, clipFacesToJoined(j, jSolved, el, solved).faces)
      }
    } catch { /* ignore */ }
  })

  if (!authority && !aligned.length) return null
  return { authority, targetSolved, aligned, seamFaces }
}

export interface CheekWallPlan {
  /** the wall path ON the high roof's bearing line — a→b ordered so the wall's
   *  EXTERIOR (left of the draw direction, the wall convention) faces OUTWARD
   *  from the high roof. Create the wall with `locationLine: 'coreFaceInterior'`
   *  and this path IS the interior core face on the bearing line. */
  a: Pt2
  b: Pt2
  /** nominal unconnected height from the HIGH roof's level — clears the high
   *  underside along the run; the top attach clips it to the exact plane */
  heightFt: number
}

/**
 * CHEEK-WALL SCAFFOLD (E5 r10, Matt's clerestory-wedge ask): plan the wall that
 * fills the triangular wedge between a LOW roof's top and a HIGH roof's
 * underside, standing on the HIGH roof's bearing line. This is a PLANNER, not a
 * generator — the caller creates a REAL wall element from it (chosen type,
 * `coreFaceInterior` location line, top attached to the high roof, base
 * attached to the low roof), so framing/openings/sections/live-follow all come
 * from the wall itself; the plan is a one-time scaffold, per the doctrine.
 *
 * Edge choice is derived: the HIGH bearing edge with the longest stretch that
 * is (a) covered by the LOW roof's footprint (the base attach needs a surface
 * to land on) and (b) an OPEN wedge (low top below high underside). Returns
 * null when no such stretch exists.
 */
/**
 * TEAR r6 (Matt: "the knee wall should automatically fill in based on the
 * attached wall type"): the SINGLE-ROOF sibling of planCheekWall below — a
 * telescoping tear inside ONE footprint stacks the upper sheet (and its flying
 * rake) over the lower gable with open air between, and the knee/gable infill
 * wall runs along the TEAR line. One plan per tear run: path on the tear
 * (ordered so the LOWER roof lies LEFT of a→b — the exterior side, matching
 * the coreFaceInterior convention), height reaching the upper surface. Both
 * attach ids point at the SAME roof: the base clips up onto the lower sheet's
 * top, the top clips under the upper sheet (deriveAttachedTop's below-base
 * surface filter is what makes the same-element pairing resolve).
 */
export function planCheekWallsForTear(el: ResolvedElement): CheekWallPlan[] {
  if (el.category !== 'roof' || !el.level) return []
  let s: ResolvedRoofSolve | null = null
  try { s = solveRoofForElement(el) } catch { return [] }
  if (!s || s.faces.length < 2) return []
  const t = el.assemblyThicknessFt
  const body = s.faces.filter((f) => !f.flying)
  // knee walls stand on TEAR lines — the gable rails' BEARING lines (in-span or
  // extended). A z-mismatched boundary along an eave OFFSET line (the overhang
  // band's edge over the lower roof) is roof-over-air, not a wall location.
  const railLines = s.specs.filter((sp) => sp.gable).map((sp) => {
    const dx = sp.b[0] - sp.a[0], dy = sp.b[1] - sp.a[1]
    const len = Math.hypot(dx, dy) || 1
    return { o: sp.a, d: [dx / len, dy / len] as Pt2 }
  })
  const onRail = (p: Pt2, q: Pt2) => railLines.some((L) =>
    Math.abs(L.d[0] * (p[1] - L.o[1]) - L.d[1] * (p[0] - L.o[0])) <= 1e-4 &&
    Math.abs(L.d[0] * (q[1] - L.o[1]) - L.d[1] * (q[0] - L.o[0])) <= 1e-4)
  const plans: CheekWallPlan[] = []
  for (const F of body) {
    for (const G of body) {
      if (F === G) continue
      for (let i = 0; i < F.ring.length; i++) {
        const P = F.ring[i], Q = F.ring[(i + 1) % F.ring.length]
        if (!onRail(P, Q)) continue
        for (let j = 0; j < G.ring.length; j++) {
          const ov = collinearOverlap(P, Q, G.ring[j], G.ring[(j + 1) % G.ring.length])
          if (!ov) continue
          const [A, B] = ov
          const len = Math.hypot(B[0] - A[0], B[1] - A[1])
          if (len < 0.5) continue
          const mid: Pt2 = [(A[0] + B[0]) / 2, (A[1] + B[1]) / 2]
          // F must be the UPPER side of a real open wedge (not a fold) — the
          // symmetric G-upper pass emits the same run once, from its own side
          if (F.zAt(mid) - (G.zAt(mid) + t * Math.sqrt(1 + G.m * G.m)) <= 0.25) continue
          // exterior (the lower roof's side) on the LEFT of a→b
          const lx = -(B[1] - A[1]) / len, ly = (B[0] - A[0]) / len
          const flip = !pointInRing([mid[0] + lx * 0.02, mid[1] + ly * 0.02], G.ring)
          const a: Pt2 = flip ? [B[0], B[1]] : [A[0], A[1]]
          const b: Pt2 = flip ? [A[0], A[1]] : [B[0], B[1]]
          plans.push({
            a, b,
            heightFt: Math.max(F.zAt(A), F.zAt(B)) + 1 - el.level.elevationFt,
          })
        }
      }
    }
  }
  // dedupe near-identical runs (adjacent overlap fragments along one tear line)
  const out: CheekWallPlan[] = []
  for (const p of plans) {
    const dup = out.some((q) =>
      Math.hypot(q.a[0] - p.a[0], q.a[1] - p.a[1]) < 0.25 &&
      Math.hypot(q.b[0] - p.b[0], q.b[1] - p.b[1]) < 0.25)
    if (!dup) out.push(p)
  }
  // chain collinear runs sharing an endpoint into ONE wall (a tear split at a
  // partition vertex is still one physical knee wall)
  for (let changed = true; changed;) {
    changed = false
    outer: for (let i = 0; i < out.length; i++) {
      for (let j = 0; j < out.length; j++) {
        if (i === j) continue
        const X = out[i], Y = out[j]
        if (Math.hypot(X.b[0] - Y.a[0], X.b[1] - Y.a[1]) > 0.05) continue
        const dx1 = X.b[0] - X.a[0], dy1 = X.b[1] - X.a[1]
        const dx2 = Y.b[0] - Y.a[0], dy2 = Y.b[1] - Y.a[1]
        if (Math.abs(dx1 * dy2 - dy1 * dx2) > 1e-3 * Math.hypot(dx1, dy1) * Math.hypot(dx2, dy2)) continue
        out[i] = { a: X.a, b: Y.b, heightFt: Math.max(X.heightFt, Y.heightFt) }
        out.splice(j, 1)
        changed = true
        break outer
      }
    }
  }
  return out
}

export function planCheekWall(high: ResolvedElement, low: ResolvedElement): CheekWallPlan | null {
  if (high.category !== 'roof' || low.category !== 'roof' || !high.level) return null
  let hs: ResolvedRoofSolve | null = null
  let ls: ResolvedRoofSolve | null = null
  try {
    hs = solveRoofForElement(high)
    ls = solveRoofForElement(low)
  } catch { return null }
  if (!hs || !ls || !hs.faces.length || !ls.faces.length || ls.eaveRing.length < 3) return null

  const tLow = low.assemblyThicknessFt
  const lowTopAt = (p: Pt2): number | null => {
    for (const f of ls.faces) {
      if (!pointInRing(p, f.ring)) continue
      return f.zAt(p) + tLow * Math.sqrt(1 + f.m * f.m)
    }
    return null
  }
  const highUnderAt = (p: Pt2): number | null => {
    let z: number | null = null
    for (const f of hs.faces) {
      if (!pointInRing(p, f.ring)) continue
      const v = f.zAt(p)
      if (z === null || v > z) z = v
    }
    return z
  }

  let best: { a: Pt2; b: Pt2; len: number; zMax: number } | null = null
  const ring = hs.ring
  const n = ring.length
  for (let i = 0; i < n; i++) {
    const A = ring[i], B = ring[(i + 1) % n]
    const dx = B[0] - A[0], dy = B[1] - A[1]
    const len = Math.hypot(dx, dy)
    if (len < 0.5) continue
    let nx = -dy / len, ny = dx / len
    const mid: Pt2 = [(A[0] + B[0]) / 2, (A[1] + B[1]) / 2]
    if (pointInRing([mid[0] + nx * 0.01, mid[1] + ny * 0.01], ring)) { nx = -nx; ny = -ny }
    // the stretches of this bearing edge covered by the LOW roof's footprint
    const unc = uncoveredIntervals(A, B, [ls.eaveRing])
    const covered: [number, number][] = []
    let cur = 0
    for (const [u0, u1] of unc) {
      if (u0 - cur > 1e-6) covered.push([cur, u0])
      cur = u1
    }
    if (1 - cur > 1e-6) covered.push([cur, 1])
    for (const [c0, c1] of covered) {
      const segLen = (c1 - c0) * len
      if (segLen < 0.5) continue
      // sample the stretch for an OPEN wedge (low top strictly below the high
      // underside, probed a hair inboard of the bearing line) — keep the
      // longest contiguous open run, snapping run ends to the stretch ends
      // when their end samples are open. MIDPOINT sampling: a grid sample can
      // land exactly on the high roof's ridge line, where the face probe is
      // ambiguous and would falsely split the run.
      const M = Math.max(9, Math.ceil(segLen))
      let run: [number, number] | null = null
      let runStart: number | null = null
      let zMax = -Infinity
      let bestRun: { t0: number; t1: number; zMax: number } | null = null
      for (let k = 0; k < M; k++) {
        const tk = c0 + ((c1 - c0) * (k + 0.5)) / M
        const p: Pt2 = [A[0] + dx * tk, A[1] + dy * tk]
        const pin: Pt2 = [p[0] - nx * 0.02, p[1] - ny * 0.02]
        const lowZ = lowTopAt(p)
        const highZ = highUnderAt(pin)
        const open = lowZ !== null && highZ !== null && highZ > lowZ + 0.05
        if (open) {
          if (runStart === null) { runStart = k === 0 ? c0 : tk; zMax = highZ }
          else if (highZ > zMax) zMax = highZ
          run = [runStart, k === M - 1 ? c1 : tk]
        }
        if ((!open || k === M - 1) && run && runStart !== null) {
          const cand = { t0: run[0], t1: run[1], zMax }
          if (!bestRun || (cand.t1 - cand.t0) > (bestRun.t1 - bestRun.t0)) bestRun = cand
          run = null
          runStart = null
          zMax = -Infinity
        }
      }
      if (!bestRun) continue
      const runLen = (bestRun.t1 - bestRun.t0) * len
      if (runLen < 0.5) continue
      if (!best || runLen > best.len) {
        // order a→b so the EXTERIOR (left of a→b) faces OUTWARD from the high
        // roof: left(d) = (−dy, dx) must equal the outward normal
        const P0: Pt2 = [A[0] + dx * bestRun.t0, A[1] + dy * bestRun.t0]
        const P1: Pt2 = [A[0] + dx * bestRun.t1, A[1] + dy * bestRun.t1]
        const d: Pt2 = [(P1[0] - P0[0]) / runLen, (P1[1] - P0[1]) / runLen]
        const leftIsOut = (-d[1]) * nx + d[0] * ny > 0
        best = leftIsOut
          ? { a: P0, b: P1, len: runLen, zMax: bestRun.zMax }
          : { a: P1, b: P0, len: runLen, zMax: bestRun.zMax }
      }
    }
  }
  if (!best) return null
  return {
    a: best.a,
    b: best.b,
    heightFt: Math.max(1, best.zMax - high.level.elevationFt + 1),
  }
}

/** Sutherland–Hodgman clip of a ring to f(p) ≥ 0 with f LINEAR (local copy of the
 *  solver's — that one is deliberately unexported solver internals). */
export function clipHalfPlane(ring: Pt2[], f: (p: Pt2) => number): Pt2[] {
  const out: Pt2[] = []
  const n = ring.length
  const EPS = 1e-9
  for (let i = 0; i < n; i++) {
    const cur = ring[i], prev = ring[(i + n - 1) % n]
    const fc = f(cur), fp = f(prev)
    if (fc >= -EPS) {
      if (fp < -EPS) {
        const t = fp / (fp - fc)
        out.push([prev[0] + (cur[0] - prev[0]) * t, prev[1] + (cur[1] - prev[1]) * t])
      }
      out.push([cur[0], cur[1]])
    } else if (fp >= -EPS) {
      const t = fp / (fp - fc)
      out.push([prev[0] + (cur[0] - prev[0]) * t, prev[1] + (cur[1] - prev[1]) * t])
    }
  }
  return out
}

/**
 * BODY JOIN: clip the joiner's solved faces against the target's TOP surface —
 * each face keeps only the region where its own top rides at or above the
 * target's; the new boundary is the VALLEY line. Coplanar-BEARING pairs are
 * skipped (aligned eaves), so the parallel telescoping case never eats its own
 * shared plane. Faces the clip never touches keep their ORIGINAL ring objects
 * (the shared-edge census stays exact); a face may split into pieces, each
 * carried forward as its own RoofFace sharing the plane. Exception-proof:
 * any failure returns the solve unclipped.
 */
export function clipFacesToJoined(
  el: ResolvedElement,
  solved: ResolvedRoofSolve,
  target: ResolvedElement,
  tgtSolved: ResolvedRoofSolve,
): ResolvedRoofSolve {
  if (!clipperReady()) return solved
  try {
    const tOwn = el.assemblyThicknessFt
    const tTgt = target.assemblyThicknessFt
    const keep = (rings: Pt2[][]) => rings.filter((r) => r.length >= 3 && Math.abs(signedArea(r)) > AREA_EPS)
    const outFaces: RoofFace[] = []
    for (const f of solved.faces) {
      const dvOwn = tOwn * Math.sqrt(1 + f.m * f.m)
      let kept: Pt2[][] = [f.ring]
      let touched = false
      for (const g of tgtSolved.faces) {
        // NEAR-coplanar BEARING planes (E5 r3 — 2e-3 ft over the target ring,
        // absorbing drawing tolerance: Matt's real pair sat 2e-4 ft apart and
        // the old exact 1e-6 test called them distinct planes, so neither rode
        // above the other and the duplicate sheet + its stray outline survived):
        // the two faces are ONE sheet of roof — the joiner's coverage yields to
        // the target's, so subtract the target's whole region.
        const hBear = (p: Pt2) => g.zAt(p) - f.zAt(p)
        let coplanar = true
        for (const v of g.ring) {
          if (Math.abs(hBear(v)) > 2e-3) { coplanar = false; break }
        }
        let sub: Pt2[][] | null = null
        if (coplanar) {
          // grow the partner region a hair (1/16″) so the drawing-tolerance
          // offset between the two boundaries can't leave ribbon residue along
          // the shared eave (the seam suppression hides the over-eaten sliver)
          const grown = inflateClosedRing(g.ring, 5e-3)
          sub = grown.length ? grown : [g.ring]
        } else {
          // the region of the target face whose TOP surface rides at/above ours…
          const dvTgt = tTgt * Math.sqrt(1 + g.m * g.m)
          const hTop = (p: Pt2) => (g.zAt(p) + dvTgt) - (f.zAt(p) + dvOwn)
          const gAbove = clipHalfPlane(g.ring, hTop)
          if (gAbove.length >= 3 && Math.abs(signedArea(gAbove)) > AREA_EPS) {
            // …RESTRICTED to the target's BEARING footprint (E5 r7, Matt's
            // report: the joiner was cut at the outer edge of the target's
            // OVERHANG, opening a gap along the wall — physically the sheet
            // passes UNDER a mere overhang band and dies at the WALL). Valleys
            // inside the body still cut exactly; a kept sliver beyond a valley
            // under the band sits below it, hidden.
            const inBody = intersectClosed([gAbove], [tgtSolved.ring])
              .filter((r) => r.length >= 3 && Math.abs(signedArea(r)) > AREA_EPS)
            if (inBody.length) sub = inBody
          }
        }
        if (!sub) continue
        const next = keep(differenceClosed(kept, sub))
        // clipper round-trips coordinates, so "did anything happen" is an AREA
        // question — an untouched face must keep its ORIGINAL ring object below
        const areaBefore = kept.reduce((s, r) => s + Math.abs(signedArea(r)), 0)
        const areaAfter = next.reduce((s, r) => s + Math.abs(signedArea(r)), 0)
        if (Math.abs(areaAfter - areaBefore) > AREA_EPS * 100) touched = true
        kept = next
        if (!kept.length) break
      }
      if (!touched) {
        outFaces.push(f)                    // original object — census-exact
        continue
      }
      for (const r of kept) outFaces.push({ ...f, ring: r })
    }
    return { ...solved, faces: outFaces }
  } catch {
    return solved
  }
}
